import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Schedule {
  id?: number;
  classId: number;
  dayOfWeek: number; // 2=Mon ... 7=Sat, 8=Sun
  startTime: string; // "HH:MM" or "HH:MM:SS"
  endTime: string;
}

interface ClassItem {
  id: number;
  name: string;
  teacherId: number;
}

interface Teacher {
  id: number;
  userName: string;
  role: number;
}

interface Props {
  schedulesList: Schedule[];
  classes: ClassItem[];
  teachers: Teacher[];
  userRole: number; // 0 = admin, 1 = teacher
  onUpdateSchedule: (classId: number, schedules: Schedule[]) => void;
}

interface PendingChange {
  classId: number;
  dayOfWeek: number;
  className: string;
  dayLabel: string;
  oldStart: string;
  oldEnd: string;
  newStart: string;
  newEnd: string;
  updatedSchedules: Schedule[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const HOUR_START = 6;
const HOUR_END = 22;
const TOTAL_HOURS = HOUR_END - HOUR_START;
const HOUR_HEIGHT = 64; // px per hour
const GRID_HEIGHT = TOTAL_HOURS * HOUR_HEIGHT;
const MIN_SNAP = 15; // snap to 15 minutes

const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
};

const minutesToTime = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const snapMinutes = (mins: number): number =>
  Math.round(mins / MIN_SNAP) * MIN_SNAP;

const formatDisplayTime = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const suffix = h < 12 ? 'SA' : 'CH';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}h ${suffix}` : `${h12}h${String(m).padStart(2, '0')} ${suffix}`;
};

// Vietnamese day names mapping: dayOfWeek (2-8) → JS day index (1-0)
// dayOfWeek: 2=Thứ2(Mon)=1, 3=Thứ3(Tue)=2, ... 7=Thứ7(Sat)=6, 8=CN(Sun)=0
const DOW_TO_COL: Record<number, number> = { 2: 0, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5, 8: 6 };
const COL_TO_DOW: Record<number, number> = { 0: 2, 1: 3, 2: 4, 3: 5, 4: 6, 5: 7, 6: 8 };

const DAY_LABELS = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'CN'];
const DAY_SHORT_VN = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

// Get Monday of the week containing `date`
const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Generate consistent color from class name
const classColor = (className: string): { bg: string; border: string; text: string } => {
  let hash = 0;
  for (let i = 0; i < className.length; i++) {
    hash = className.charCodeAt(i) + ((hash << 5) - hash);
  }
  const palettes = [
    { bg: '#e3f2fd', border: '#1976d2', text: '#0d47a1' },
    { bg: '#f3e5f5', border: '#7b1fa2', text: '#4a148c' },
    { bg: '#e8f5e9', border: '#388e3c', text: '#1b5e20' },
    { bg: '#fff3e0', border: '#f57c00', text: '#e65100' },
    { bg: '#fce4ec', border: '#c2185b', text: '#880e4f' },
    { bg: '#e0f7fa', border: '#0097a7', text: '#006064' },
    { bg: '#f9fbe7', border: '#827717', text: '#558b2f' },
    { bg: '#ede7f6', border: '#512da8', text: '#311b92' },
    { bg: '#e1f5fe', border: '#0288d1', text: '#01579b' },
    { bg: '#fbe9e7', border: '#d84315', text: '#bf360c' },
  ];
  return palettes[Math.abs(hash) % palettes.length];
};

// ─── Styled Components ───────────────────────────────────────────────────────
const fadeIn = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }`;

const CalendarWrapper = styled.div`
  animation: ${fadeIn} 0.35s ease-out;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  overflow: hidden;
  border: 1px solid #e8eaf0;
`;

const CalendarToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-wrap: wrap;
`;

const ToolbarLabel = styled.span`
  color: rgba(255,255,255,0.85);
  font-size: 0.82rem;
  font-weight: 600;
  white-space: nowrap;
`;

const WeekNav = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.15);
  border-radius: 8px;
  padding: 4px 8px;
`;

const NavBtn = styled.button`
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,0.35); }
`;

const WeekLabel = styled.span`
  color: white;
  font-weight: 700;
  font-size: 0.88rem;
  white-space: nowrap;
  min-width: 140px;
  text-align: center;
`;

const TeacherSelect = styled.select`
  background: rgba(255,255,255,0.15);
  border: 1.5px solid rgba(255,255,255,0.3);
  color: white;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  option { color: #333; background: white; }
  &:focus { border-color: white; }
`;

const GridContainer = styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 280px);
  min-height: 400px;
  position: relative;
`;

const GridInner = styled.div`
  display: flex;
  min-width: 700px;
  position: relative;
`;

const TimeColumn = styled.div`
  width: 54px;
  flex-shrink: 0;
  background: #fafbfc;
  border-right: 1px solid #e8eaf0;
  position: sticky;
  left: 0;
  z-index: 3;
`;

const TimeSlot = styled.div<{ $first?: boolean }>`
  height: ${HOUR_HEIGHT}px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding-right: 8px;
  padding-top: ${p => p.$first ? 0 : 6}px;
  font-size: 0.7rem;
  color: #9e9e9e;
  font-weight: 600;
  border-top: ${p => p.$first ? 'none' : '1px solid #f0f1f4'};
  box-sizing: border-box;
  user-select: none;
`;

const DaysArea = styled.div`
  flex: 1;
  position: relative;
`;

const DaysHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  position: sticky;
  top: 0;
  z-index: 4;
  background: white;
  border-bottom: 2px solid #e8eaf0;
`;

const DayHeaderCell = styled.div<{ $isToday?: boolean }>`
  text-align: center;
  padding: 10px 4px 8px;
  font-size: 0.78rem;
  font-weight: 700;
  border-left: 1px solid #e8eaf0;
  color: ${p => p.$isToday ? '#1976d2' : '#546e7a'};
  background: ${p => p.$isToday ? '#e3f2fd' : 'white'};
  user-select: none;
`;

const DayNum = styled.div<{ $isToday?: boolean }>`
  font-size: 1rem;
  font-weight: 800;
  color: ${p => p.$isToday ? '#fff' : '#37474f'};
  background: ${p => p.$isToday ? '#1976d2' : 'transparent'};
  width: ${p => p.$isToday ? '28px' : 'auto'};
  height: ${p => p.$isToday ? '28px' : 'auto'};
  border-radius: 50%;
  display: ${p => p.$isToday ? 'flex' : 'block'};
  align-items: center;
  justify-content: center;
  margin: ${p => p.$isToday ? '2px auto 0' : '2px auto 0'};
`;

const GridColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  position: relative;
  height: ${GRID_HEIGHT}px;
`;

const DayColumn = styled.div<{ $isToday?: boolean }>`
  position: relative;
  border-left: 1px solid #e8eaf0;
  background: ${p => p.$isToday ? 'rgba(25, 118, 210, 0.02)' : 'transparent'};
  /* Hour lines */
  background-image: repeating-linear-gradient(
    to bottom,
    transparent,
    transparent ${HOUR_HEIGHT - 1}px,
    #f0f1f4 ${HOUR_HEIGHT - 1}px,
    #f0f1f4 ${HOUR_HEIGHT}px
  );
`;

const HalfLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px dashed #e8eaf0;
  pointer-events: none;
`;

const EventBlock = styled.div<{
  $top: number;
  $height: number;
  $bg: string;
  $border: string;
  $text: string;
  $isDragging: boolean;
  $col: number;
  $totalCols: number;
  $canDrag: boolean;
}>`
  position: absolute;
  top: ${p => p.$top}px;
  height: ${p => Math.max(p.$height, 22)}px;
  left: ${p => (p.$col / p.$totalCols) * 100 + 1}%;
  width: ${p => (1 / p.$totalCols) * 100 - 2}%;
  background: ${p => p.$bg};
  border-left: 3px solid ${p => p.$border};
  border-radius: 6px;
  padding: 4px 6px 2px;
  box-shadow: ${p => p.$isDragging
    ? `0 8px 24px rgba(0,0,0,0.22), 0 0 0 2px ${p.$border}`
    : '0 1px 4px rgba(0,0,0,0.1)'};
  cursor: ${p => p.$canDrag ? 'grab' : 'default'};
  user-select: none;
  transition: ${p => p.$isDragging ? 'none' : 'box-shadow 0.2s'};
  z-index: ${p => p.$isDragging ? 100 : 2};
  overflow: hidden;
  opacity: ${p => p.$isDragging ? 0.88 : 1};
  transform: ${p => p.$isDragging ? 'scale(1.01)' : 'scale(1)'};
  &:hover { box-shadow: ${p => p.$canDrag ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 4px rgba(0,0,0,0.1)'}; z-index: 10; }
`;

const EventTitle = styled.div<{ $color: string }>`
  font-size: 0.72rem;
  font-weight: 700;
  color: ${p => p.$color};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EventTime = styled.div<{ $color: string }>`
  font-size: 0.65rem;
  color: ${p => p.$color};
  opacity: 0.8;
  margin-top: 2px;
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 8px;
  cursor: ns-resize;
  background: linear-gradient(transparent, rgba(0,0,0,0.08));
  border-radius: 0 0 6px 6px;
  &:hover { background: linear-gradient(transparent, rgba(0,0,0,0.18)); }
`;

const CurrentTimeLine = styled.div<{ $top: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${p => p.$top}px;
  z-index: 5;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const CurrentTimeDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f44336;
  flex-shrink: 0;
  margin-left: -5px;
`;

const CurrentTimeLine2 = styled.div`
  flex: 1;
  height: 2px;
  background: #f44336;
  opacity: 0.85;
`;

const EmptyHint = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bdbdbd;
  font-size: 0.85rem;
  font-style: italic;
  pointer-events: none;
`;

// ─── Confirm Modal Overlay ────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.18s ease-out;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  padding: 28px 32px;
  min-width: 360px;
  max-width: 480px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 1.1rem;
  font-weight: 800;
  color: #1a237e;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalBody = styled.div`
  font-size: 0.92rem;
  color: #37474f;
  line-height: 1.7;
  margin-bottom: 20px;
`;

const ModalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f5f7ff;
  border: 1px solid #e3e8ff;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ModalCancelBtn = styled.button`
  padding: 8px 20px;
  border: 1.5px solid #cfd8dc;
  background: white;
  color: #546e7a;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #f5f5f5; }
`;

const ModalConfirmBtn = styled.button`
  padding: 8px 22px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102,126,234,0.4);
  transition: all 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-1px); }
`;

// ─── Component ───────────────────────────────────────────────────────────────
const ScheduleCalendarView: React.FC<Props> = ({
  schedulesList,
  classes,
  teachers,
  userRole,
  onUpdateSchedule,
}) => {
  const isAdmin = userRole === 0;
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()));
  const [filterTeacherId, setFilterTeacherId] = useState<string>('');
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  // Confirmation modal state (admin only)
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);

  // Drag state
  const dragRef = useRef<{
    scheduleId: number | undefined;
    classId: number;
    dayOfWeek: number;
    originalStart: number; // minutes
    originalEnd: number;
    startY: number;
    mode: 'move' | 'resize';
  } | null>(null);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [dragPreview, setDragPreview] = useState<Record<string, { startTime: string; endTime: string }>>({});
  const gridRef = useRef<HTMLDivElement>(null);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  // Week dates
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  const today = new Date();
  const isToday = (d: Date) =>
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();

  const isSameWeekAsToday = weekDates.some(isToday);

  // Current time line position
  const nowMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
  const nowTop = ((nowMinutes - HOUR_START * 60) / 60) * HOUR_HEIGHT;

  // Format week label
  const formatWeekLabel = () => {
    const end = weekDates[6];
    const fmt = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}`;
    if (weekStart.getMonth() === end.getMonth()) {
      return `${weekStart.getDate()} – ${end.getDate()}/${end.getMonth() + 1}/${end.getFullYear()}`;
    }
    return `${fmt(weekStart)} – ${fmt(end)}/${end.getFullYear()}`;
  };

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };
  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };
  const goToday = () => setWeekStart(getWeekStart(new Date()));

  // Filter schedules by teacher
  const filteredSchedules = schedulesList.filter(s => {
    if (!filterTeacherId) return true;
    const cls = classes.find(c => c.id === s.classId);
    return cls && String(cls.teacherId) === filterTeacherId;
  });

  // Group overlapping events per day column
  const buildEventLayout = () => {
    // Map: dayCol (0-6) → list of events with layout info
    const dayEvents: Record<number, { schedule: Schedule; className: string; col: number; totalCols: number }[]> = {};
    for (let i = 0; i < 7; i++) dayEvents[i] = [];

    filteredSchedules.forEach(s => {
      const col = DOW_TO_COL[s.dayOfWeek];
      if (col === undefined) return;
      const cls = classes.find(c => c.id === s.classId);
      if (!cls) return;
      dayEvents[col].push({ schedule: s, className: cls.name, col: 0, totalCols: 1 });
    });

    // Simple overlap detection per column
    Object.keys(dayEvents).forEach(colKey => {
      const events = dayEvents[Number(colKey)];
      events.sort((a, b) => timeToMinutes(a.schedule.startTime) - timeToMinutes(b.schedule.startTime));

      // Build overlap groups
      const groups: typeof events[] = [];
      events.forEach(ev => {
        const startM = timeToMinutes(ev.schedule.startTime);
        const endM = timeToMinutes(ev.schedule.endTime);
        let placed = false;
        for (const group of groups) {
          const maxEnd = Math.max(...group.map(e => timeToMinutes(e.schedule.endTime)));
          if (startM < maxEnd) {
            ev.col = group.length;
            group.push(ev);
            placed = true;
            break;
          }
        }
        if (!placed) {
          ev.col = 0;
          groups.push([ev]);
        }
      });
      // Assign totalCols
      groups.forEach(group => {
        const total = group.length;
        group.forEach(ev => { ev.totalCols = total; });
      });
    });

    return dayEvents;
  };

  const eventLayout = buildEventLayout();

  // Get current preview times for a schedule
  const getEffectiveTimes = (s: Schedule) => {
    const key = `${s.classId}-${s.dayOfWeek}`;
    const preview = dragPreview[key];
    return preview || { startTime: s.startTime, endTime: s.endTime };
  };

  // Pixel offset inside grid → minutes from midnight
  const yToMinutes = useCallback((y: number): number => {
    const minutesFromStart = (y / HOUR_HEIGHT) * 60;
    return snapMinutes(HOUR_START * 60 + minutesFromStart);
  }, []);

  // ── Mouse drag handlers ──
  const handleMouseDown = useCallback((
    e: React.MouseEvent,
    schedule: Schedule,
    mode: 'move' | 'resize',
  ) => {
    if (!isAdmin) return; // Chỉ admin mới được kéo thả
    e.preventDefault();
    e.stopPropagation();
    const key = `${schedule.classId}-${schedule.dayOfWeek}`;
    const startM = timeToMinutes(schedule.startTime);
    const endM = timeToMinutes(schedule.endTime);
    dragRef.current = {
      scheduleId: schedule.id,
      classId: schedule.classId,
      dayOfWeek: schedule.dayOfWeek,
      originalStart: startM,
      originalEnd: endM,
      startY: e.clientY,
      mode,
    };
    setDraggingKey(key);
    setDragPreview(prev => ({ ...prev, [key]: { startTime: schedule.startTime, endTime: schedule.endTime } }));
  }, [isAdmin]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current || !gridRef.current) return;
    const { classId, dayOfWeek, originalStart, originalEnd, startY, mode } = dragRef.current;
    const key = `${classId}-${dayOfWeek}`;
    const deltaY = e.clientY - startY;
    const deltaMinutes = snapMinutes((deltaY / HOUR_HEIGHT) * 60);
    const duration = originalEnd - originalStart;

    let newStart: number, newEnd: number;
    if (mode === 'move') {
      newStart = Math.max(HOUR_START * 60, Math.min(originalStart + deltaMinutes, HOUR_END * 60 - 30));
      newEnd = newStart + duration;
      if (newEnd > HOUR_END * 60) { newEnd = HOUR_END * 60; newStart = newEnd - duration; }
    } else {
      newStart = originalStart;
      newEnd = Math.max(originalStart + 30, Math.min(originalEnd + deltaMinutes, HOUR_END * 60));
    }

    setDragPreview(prev => ({
      ...prev,
      [key]: { startTime: minutesToTime(newStart), endTime: minutesToTime(newEnd) },
    }));
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!dragRef.current) return;
    const { classId, dayOfWeek, originalStart, originalEnd } = dragRef.current;
    const key = `${classId}-${dayOfWeek}`;
    const preview = dragPreview[key];

    if (preview) {
      const newStartM = timeToMinutes(preview.startTime);
      const newEndM = timeToMinutes(preview.endTime);
      // Chỉ mở modal nếu thời gian thực sự thay đổi
      if (newStartM !== originalStart || newEndM !== originalEnd) {
        const allForClass = schedulesList.filter(s => s.classId === classId);
        const updatedSchedules = allForClass.map(s =>
          s.dayOfWeek === dayOfWeek
            ? { ...s, startTime: preview.startTime, endTime: preview.endTime }
            : s,
        );
        const cls = schedulesList.find(s => s.classId === classId);
        const classItem = classes.find(c => c.id === classId);
        const DOW_VN: Record<number, string> = { 2: 'Thứ Hai', 3: 'Thứ Ba', 4: 'Thứ Tư', 5: 'Thứ Năm', 6: 'Thứ Sáu', 7: 'Thứ Bảy', 8: 'Chủ Nhật' };
        setPendingChange({
          classId,
          dayOfWeek,
          className: classItem?.name || `Lớp #${classId}`,
          dayLabel: DOW_VN[dayOfWeek] || '',
          oldStart: minutesToTime(originalStart),
          oldEnd: minutesToTime(originalEnd),
          newStart: preview.startTime,
          newEnd: preview.endTime,
          updatedSchedules,
        });
        // Giữ preview để block vẫn hiện ở vị trí mới khi modal mở
        dragRef.current = null;
        setDraggingKey(null);
        return;
      }
    }

    dragRef.current = null;
    setDraggingKey(null);
    setDragPreview({});
  }, [dragPreview, schedulesList, classes, onUpdateSchedule]);

  // Xác nhận thay đổi lịch
  const handleConfirmChange = useCallback(() => {
    if (!pendingChange) return;
    onUpdateSchedule(pendingChange.classId, pendingChange.updatedSchedules);
    setPendingChange(null);
    setDragPreview({});
  }, [pendingChange, onUpdateSchedule]);

  // Hủy thay đổi
  const handleCancelChange = useCallback(() => {
    setPendingChange(null);
    setDragPreview({});
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const availableTeachers = teachers.filter(t => {
    if (t.role !== 0 && t.role !== 1) return false;
    return schedulesList.some(s => {
      const cls = classes.find(c => c.id === s.classId);
      return cls && cls.teacherId === t.id;
    });
  });

  const hasAnyEvent = filteredSchedules.length > 0;

  return (
    <CalendarWrapper>
      {/* Toolbar */}
      <CalendarToolbar>
        {/* Week navigation */}
        <WeekNav>
          <NavBtn onClick={prevWeek} title="Tuần trước">◀</NavBtn>
          <WeekLabel>{formatWeekLabel()}</WeekLabel>
          <NavBtn onClick={nextWeek} title="Tuần sau">▶</NavBtn>
        </WeekNav>

        {!isSameWeekAsToday && (
          <NavBtn
            onClick={goToday}
            title="Về tuần hiện tại"
            style={{ background: 'rgba(255,255,255,0.25)', width: 'auto', padding: '0 10px', fontSize: '0.78rem', fontWeight: 700 }}
          >
            Hôm nay
          </NavBtn>
        )}

        {/* Teacher filter */}
        <ToolbarLabel>👨‍🏫 GV:</ToolbarLabel>
        <TeacherSelect value={filterTeacherId} onChange={e => setFilterTeacherId(e.target.value)}>
          <option value="">Tất cả giáo viên</option>
          {[...availableTeachers]
            .sort((a, b) => (a.userName || '').localeCompare(b.userName || ''))
            .map(t => (
              <option key={t.id} value={String(t.id)}>{t.userName}</option>
            ))}
        </TeacherSelect>

        <ToolbarLabel style={{ marginLeft: 'auto', opacity: 0.7, fontSize: '0.75rem' }}>
          {isAdmin ? '💡 Kéo thả để điều chỉnh giờ học' : '👁 Chế độ xem (không có quyền chỉnh sửa)'}
        </ToolbarLabel>
      </CalendarToolbar>

      {/* Grid */}
      <GridContainer ref={gridRef}>
        <GridInner>
          {/* Time column — header spacer */}
          <TimeColumn>
            <div style={{ height: 52, borderBottom: '2px solid #e8eaf0', background: '#fafbfc' }} />
            {Array.from({ length: TOTAL_HOURS }, (_, i) => {
              const hour = HOUR_START + i;
              return (
                <TimeSlot key={hour} $first={i === 0}>
                  {hour}:00
                </TimeSlot>
              );
            })}
          </TimeColumn>

          {/* Days area */}
          <DaysArea>
            {/* Day headers */}
            <DaysHeader>
              {weekDates.map((d, col) => {
                const today_ = isToday(d);
                return (
                  <DayHeaderCell key={col} $isToday={today_}>
                    {DAY_LABELS[col]}
                    <DayNum $isToday={today_}>{d.getDate()}</DayNum>
                    <div style={{ fontSize: '0.65rem', color: '#9e9e9e', marginTop: 2 }}>
                      {d.getMonth() + 1}/{d.getFullYear().toString().slice(2)}
                    </div>
                  </DayHeaderCell>
                );
              })}
            </DaysHeader>

            {/* Grid body */}
            <GridColumns>
              {/* Column per day */}
              {weekDates.map((d, col) => {
                const today_ = isToday(d);
                const events = eventLayout[col] || [];
                return (
                  <DayColumn key={col} $isToday={today_}>
                    {/* Half-hour dashed lines */}
                    {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                      <HalfLine key={i} style={{ top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2 }} />
                    ))}

                    {/* Current time line — only in today's column */}
                    {today_ && nowTop >= 0 && nowTop <= GRID_HEIGHT && (
                      <CurrentTimeLine $top={nowTop}>
                        <CurrentTimeDot />
                        <CurrentTimeLine2 />
                      </CurrentTimeLine>
                    )}

                    {/* Events */}
                    {events.map(({ schedule: s, className, col: eCol, totalCols }) => {
                      const key = `${s.classId}-${s.dayOfWeek}`;
                      const { startTime, endTime } = getEffectiveTimes(s);
                      const startM = timeToMinutes(startTime);
                      const endM = timeToMinutes(endTime);
                      const top = ((startM - HOUR_START * 60) / 60) * HOUR_HEIGHT;
                      const height = ((endM - startM) / 60) * HOUR_HEIGHT;
                      const color = classColor(className);
                      const isDragging = draggingKey === key;

                      return (
                        <EventBlock
                          key={key}
                          $top={top}
                          $height={height}
                          $bg={color.bg}
                          $border={color.border}
                          $text={color.text}
                          $isDragging={isDragging}
                          $col={eCol}
                          $totalCols={totalCols}
                          $canDrag={isAdmin}
                          onMouseDown={e => handleMouseDown(e, s, 'move')}
                          title={isAdmin
                            ? `${className}\n${formatDisplayTime(startTime)} → ${formatDisplayTime(endTime)}\nKéo để điều chỉnh giờ`
                            : `${className}\n${formatDisplayTime(startTime)} → ${formatDisplayTime(endTime)}`
                          }
                        >
                          <EventTitle $color={color.text}>{className}</EventTitle>
                          {height >= 36 && (
                            <EventTime $color={color.text}>
                              {formatDisplayTime(startTime)} – {formatDisplayTime(endTime)}
                            </EventTime>
                          )}
                          {/* Resize handle — chỉ hiện cho admin */}
                          {isAdmin && (
                            <ResizeHandle
                              onMouseDown={e => {
                                e.stopPropagation();
                                handleMouseDown(e as any, s, 'resize');
                              }}
                              title="Kéo để điều chỉnh giờ kết thúc"
                            />
                          )}
                        </EventBlock>
                      );
                    })}
                  </DayColumn>
                );
              })}

              {!hasAnyEvent && (
                <EmptyHint style={{ gridColumn: '1 / -1' }}>
                  Không có ca học nào để hiển thị
                </EmptyHint>
              )}
            </GridColumns>
          </DaysArea>
        </GridInner>
      </GridContainer>

      {/* ── Confirmation Modal (Admin only) ── */}
      {pendingChange && (
        <ModalOverlay onClick={handleCancelChange}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>📅 Xác nhận thay đổi lịch học</ModalTitle>
            <ModalBody>
              <div style={{ marginBottom: 12 }}>
                Bạn có chắc muốn thay đổi lịch học ca <strong>{pendingChange.dayLabel}</strong> của lớp{' '}
                <strong style={{ color: '#7b1fa2' }}>{pendingChange.className}</strong>?
              </div>
              <ModalRow>
                <span style={{ fontSize: '1rem' }}>🕐</span>
                <div>
                  <div style={{ color: '#888', fontSize: '0.78rem', marginBottom: 2 }}>Giờ cũ</div>
                  <strong style={{ color: '#c62828', textDecoration: 'line-through' }}>
                    {formatDisplayTime(pendingChange.oldStart)} → {formatDisplayTime(pendingChange.oldEnd)}
                  </strong>
                </div>
              </ModalRow>
              <div style={{ textAlign: 'center', color: '#9e9e9e', fontSize: '1.2rem', margin: '4px 0' }}>↓</div>
              <ModalRow style={{ background: '#f0fff4', borderColor: '#c8e6c9' }}>
                <span style={{ fontSize: '1rem' }}>✅</span>
                <div>
                  <div style={{ color: '#888', fontSize: '0.78rem', marginBottom: 2 }}>Giờ mới</div>
                  <strong style={{ color: '#2e7d32' }}>
                    {formatDisplayTime(pendingChange.newStart)} → {formatDisplayTime(pendingChange.newEnd)}
                  </strong>
                </div>
              </ModalRow>
              <div style={{ marginTop: 12, fontSize: '0.78rem', color: '#999', fontStyle: 'italic' }}>
                ⚠️ Thay đổi này sẽ áp dụng cho toàn bộ lịch học định kỳ ({pendingChange.dayLabel}).
              </div>
            </ModalBody>
            <ModalFooter>
              <ModalCancelBtn onClick={handleCancelChange}>Hủy</ModalCancelBtn>
              <ModalConfirmBtn onClick={handleConfirmChange}>✅ Xác nhận thay đổi</ModalConfirmBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}
    </CalendarWrapper>
  );
};

export default ScheduleCalendarView;
